import { ingredientForSize, type PizzaSize } from '@agos/shared'

/**
 * Turns the per-size form entries into what the API expects.
 *
 * The delicate half is the legacy mirror. `price32`, `price50`, `unitLabel32`
 * and `unitLabel50` are `NOT NULL` columns that predate dynamic sizes, so they
 * have to carry a value even for a franchise that has switched 32 and 50 off
 * and runs 40 and 45. They are derived from the `sizeData` being written, via
 * `ingredientForSize`, which falls back to the nearest costed size — §14.6.
 *
 * Writing `sizeData` without the mirror would violate the column constraints;
 * writing the mirror without `sizeData` would lose every size but two.
 */

export interface SizeFormEntry {
  price: string
  unitLabel: string
  weightG: string
  cost: string
}

export const emptySizeEntry = (): SizeFormEntry => ({
  price: '',
  unitLabel: '',
  weightG: '',
  cost: '',
})

interface SizeDataEntry {
  price?: number
  unitLabel?: string | null
  weightG?: number
  cost?: number | null
}

export function buildSizePayload(
  entries: Record<string, SizeFormEntry>,
  sizes: PizzaSize[],
  includeCost: boolean,
) {
  const sizeData: Record<string, SizeDataEntry> = {}

  for (const size of sizes) {
    const entry = entries[String(size.cm)]
    if (!entry) continue

    sizeData[String(size.cm)] = {
      price: entry.price.trim() === '' ? 0 : Number(entry.price) || 0,
      unitLabel: entry.unitLabel.trim() || null,
      ...(entry.weightG.trim() !== '' ? { weightG: Number(entry.weightG) || 0 } : {}),
      ...(includeCost
        ? { cost: entry.cost.trim() === '' ? null : Number(entry.cost) || 0 }
        : {}),
    }
  }

  const nearest32 = ingredientForSize({ sizeData }, 32)
  const nearest50 = ingredientForSize({ sizeData }, 50)

  return {
    sizeData,
    price32: sizeData['32']?.price ?? nearest32.price,
    price50: sizeData['50']?.price ?? nearest50.price,
    unitLabel32: sizeData['32']?.unitLabel ?? null,
    unitLabel50: sizeData['50']?.unitLabel ?? null,
    ...(sizeData['32']?.weightG != null
      ? { weightG32: Math.round(sizeData['32'].weightG) }
      : {}),
    ...(sizeData['50']?.weightG != null
      ? { weightG50: Math.round(sizeData['50'].weightG) }
      : {}),
    ...(includeCost
      ? { cost32: sizeData['32']?.cost ?? null, cost50: sizeData['50']?.cost ?? null }
      : {}),
  }
}
